import * as React from 'react';
import type { ExecutionResult, FetchResult } from 'react-apollo';
import { Mutation } from 'react-apollo';

import type { DataProxy } from 'apollo-cache';

import { isAssignmentGradingInPublicEnabled } from 'bundles/grading-common/utils/epicUtils';
import updateGradeVisibilityMutation from 'bundles/grading/api/UpdateGradeVisibilityMutation.graphql';
import type { UpdateGradeVisibilityMutation, UpdateGradeVisibilityMutationVariables } from 'bundles/grading/api/types';
import { ItemType } from 'bundles/grading/api/types';
import type { FetchStatus } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import {
  generateSubmissionDataQueryVariables,
  updateCachedGradeVisibility,
  updateCachedGradeVisibilityV2,
} from 'bundles/grading/utils/apiUtils';
import {
  getLegacySubmitterIdFromNewId,
  getScopedSubmitterIdFromRouteSubmitterId,
} from 'bundles/grading/utils/submitterUtils';

export type GradeVisibilityMutationInjectedProps = {
  updateGradeVisibility: (param: {
    variables: UpdateGradeVisibilityMutationVariables;
  }) => Promise<void | FetchResult<UpdateGradeVisibilityMutation, Record<string, $TSFixMe>>>;
  updateGradeVisibilityStatus: FetchStatus;
};

type Props = {
  courseId: string;
  children: (gradeVisibilityMutations: GradeVisibilityMutationInjectedProps) => React.ReactElement | null;
};

/**
 * Feature mutation wrapper handling:
 *  - UpdateGradeVisibility mutation
 */
const GradeVisibilityMutationWrapper = ({ courseId, children }: Props) => {
  const onUpdateGradeVisibilityMutationComplete = (
    cache: DataProxy,
    { data }: ExecutionResult<UpdateGradeVisibilityMutation>
  ) => {
    if (data?.GroupGrading_UpdateGradeVisibility) {
      const useApiV2 = isAssignmentGradingInPublicEnabled();

      if (useApiV2) {
        const routeSubmitterId = getLegacySubmitterIdFromNewId(
          data?.GroupGrading_UpdateGradeVisibility.submissionReference.submitterId,
          data?.GroupGrading_UpdateGradeVisibility.submissionReference.groupId
        );
        const scopedSubmitterId = getScopedSubmitterIdFromRouteSubmitterId(routeSubmitterId);
        updateCachedGradeVisibilityV2(cache, data, {
          itemId: data?.GroupGrading_UpdateGradeVisibility.submissionReference.itemId,
          courseId,
          scopedSubmitterId,
        });
      } else {
        updateCachedGradeVisibility(
          cache,
          data,
          generateSubmissionDataQueryVariables(
            data?.GroupGrading_UpdateGradeVisibility.submissionReference.groupId,
            data?.GroupGrading_UpdateGradeVisibility.submissionReference.itemId,
            ItemType.StaffGradedAssignment,
            data?.GroupGrading_UpdateGradeVisibility.submissionReference.submitterId
          )
        );
      }
    }
  };

  return (
    <Mutation<UpdateGradeVisibilityMutation, UpdateGradeVisibilityMutationVariables>
      mutation={updateGradeVisibilityMutation}
      context={{ clientName: 'gatewayGql' }}
      update={onUpdateGradeVisibilityMutationComplete}
    >
      {(updateGradeVisibility, updateGradeVisibilityResult) => {
        const updateGradeVisibilityStatus: FetchStatus = {
          loading: updateGradeVisibilityResult.loading,
          error: updateGradeVisibilityResult.error,
        };
        const gradeVisibilityMutations = {
          updateGradeVisibility,
          updateGradeVisibilityStatus,
        };
        return children(gradeVisibilityMutations);
      }}
    </Mutation>
  );
};

export default GradeVisibilityMutationWrapper;
